import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageMeta from "../components/PageMeta";
import { useLanguage } from "../i18n/LanguageProvider";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "https://app.wonyaso.com"
).replace(/\/$/, "");

function localizedValue(item, field, language) {
  const preferred = item[`${field}${language === "en" ? "En" : "Fr"}`];
  const fallback = item[`${field}${language === "en" ? "Fr" : "En"}`];
  return preferred || fallback || "";
}

export default function BlogDetail() {
  const { id } = useParams();
  const { language } = useLanguage();
  const [state, setState] = useState({ loading: true, item: null, error: "" });

  useEffect(() => {
    window.scrollTo(0, 0);

    if (process.env.NODE_ENV === "test") {
      setState({ loading: false, item: null, error: "" });
      return undefined;
    }

    const controller = new AbortController();
    fetch(`${API_BASE_URL}/public/site-content/blog`, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Article unavailable");
        return response.json();
      })
      .then((response) => {
        const item = response?.data?.enabled
          ? response.data.items?.find((entry) => entry.id === id)
          : null;
        setState({
          loading: false,
          item: item || null,
          error: item
            ? ""
            : language === "en"
              ? "This article is not available."
              : "Cet article n’est pas disponible.",
        });
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setState({
            loading: false,
            item: null,
            error:
              language === "en"
                ? "Unable to load this article."
                : "Impossible de charger cet article.",
          });
        }
      });

    return () => controller.abort();
  }, [id, language]);

  if (state.loading) {
    return (
      <div className="ws-blog-detail ws-shell" aria-live="polite">
        <p>
          {language === "en" ? "Loading article…" : "Chargement de l’article…"}
        </p>
      </div>
    );
  }

  if (!state.item) {
    return (
      <div className="ws-blog-detail ws-shell">
        <h1>
          {language === "en" ? "Article unavailable" : "Article indisponible"}
        </h1>
        <p>{state.error}</p>
        <Link className="ws-btn ws-btn-primary" to="/">
          {language === "en" ? "Back to home" : "Retour à l’accueil"}
        </Link>
      </div>
    );
  }

  const title = localizedValue(state.item, "title", language);
  const subtitle = localizedValue(state.item, "subtitle", language);
  const body = localizedValue(state.item, "body", language);
  const paragraphs = body.split(/\n{2,}/).filter(Boolean);

  return (
    <article className="ws-blog-detail">
      <PageMeta
        title={`${title} | WonyaSoft`}
        description={paragraphs[0] || title}
        image={state.item.mediaUrl || undefined}
        imageAlt={title}
      />
      <header className="ws-blog-detail__hero">
        <div className="ws-shell">
          <Link className="ws-blog-detail__back" to="/#managed-blog-title">
            <span aria-hidden="true">←</span>
            {language === "en" ? "Wonya News" : "Actualités Wonya"}
          </Link>
          {subtitle && <span className="ws-kicker">{subtitle}</span>}
          <h1>{title}</h1>
        </div>
      </header>

      <div className="ws-shell ws-blog-detail__layout">
        {state.item.mediaUrl &&
          (state.item.mediaType === "video" ? (
            <video
              className="ws-blog-detail__media"
              controls
              preload="metadata"
              aria-label={title}
              src={state.item.mediaUrl}
            />
          ) : (
            <img
              className="ws-blog-detail__media"
              src={state.item.mediaUrl}
              alt={title}
              width="1586"
              height="992"
            />
          ))}
        <div className="ws-blog-detail__content">
          {paragraphs.map((paragraph, index) => (
            <p key={`${state.item.id}-${index}`}>{paragraph}</p>
          ))}
          <Link className="ws-btn ws-btn-primary" to="/solutions">
            {language === "en"
              ? "Explore our solutions"
              : "Explorer nos solutions"}
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </article>
  );
}
